import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';

import { Widget } from '@xsolla/login-sdk';

import { getCustomStyleURL, getLoginWidgetParams, getOAuthSettings } from '../../../api';
import { ChecklistItems } from '../../../api/configurations/checklist/types';
import { CLASSIC_LOGIN, PASSWORDLESS_LOGIN, SOCIAL_LOGIN } from '../../../constants';
import { useChecklist, useCurrentProject } from '../../../contexts/settings';
import { useNotifications } from '../../../hooks/useNotifications';
import { usePaths } from '../../../routes/routesHelper';
import { RouteParams } from '../../../routes/types';
import { VerticalHorizontalCenterBlock } from '../../../styled';
import { AgreementNotification, CodeEditor, HeaderTitle, Preloader, Separator } from '../../ui';
import SettingsBar from '../HTMLEditorFrame';
import { init, initialState, widgetCustomizationReducer } from './reducer';
import { getExampleCode, updatedWidgetLocale } from './settings';
import { CodeEditorFrame, CustomizationFrameWrapper, WidgetFrame } from './styles';
import { ActionType } from './types';
import VisualSettings from './VisualSettings';
import { WidgetSettings } from './WidgetSettings';

const LoginMethods = {
  [PASSWORDLESS_LOGIN]: 'passwordless',
  [CLASSIC_LOGIN]: 'classic',
  [SOCIAL_LOGIN]: 'social'
};

type Props = RouteComponentProps & {
  isAgreementSigned: boolean;
};

const WidgetCustomizeFrame = ({ match, history, isAgreementSigned }: Props): JSX.Element => {
  const { widgetEditorPath } = usePaths();
  const intl = useIntl();

  const { projectId } = match.params as RouteParams;
  const { currentProjectType } = useCurrentProject(projectId);
  const { showErrorNotify } = useNotifications();
  const { enableChecklistItem } = useChecklist(projectId);

  const [state, dispatch] = useReducer(widgetCustomizationReducer, initialState, () =>
    init(isAgreementSigned, intl)
  );
  const { fetching, isPreview, widgetPreviewSettings, oauth2 } = state;
  const { language, country } = widgetPreviewSettings;

  const [widget, setWidget] = useState<Widget>();
  const widgetContainerRef = useRef<HTMLDivElement | null>(null);

  const additionalParams = useMemo(
    () => ({
      ...(oauth2.currOAuth2 && {
        clientId: oauth2.currOAuth2.id ? oauth2.currOAuth2.id : undefined,
        responseType: `${oauth2.currOAuth2.response_types[0]}`,
        state: 'yourStateHere',
        redirectUri: `${oauth2.currOAuth2.redirect_uris[0]}`
      })
    }),
    [oauth2.currOAuth2]
  );

  useEffect(() => {
    if (widget) {
      widget.close();
    }
    if (isPreview) {
      setWidget(
        new Widget({
          projectId,
          url: process.env.LOGIN_WIDGET_URL || 'https://login-widget.xsolla.com/latest',
          preferredLocale: ['cn', 'tw'].includes(language)
            ? `zh_${language.toUpperCase()}`
            : `${language}_${country}`,
          disableCache: true,
          // settingsJSON: loginMethodsCdnUrl[currentProjectType],
          isDemo: true,
          ...additionalParams
        })
      );
    }
  }, [isPreview, language, country, additionalParams]);

  useEffect(() => {
    if (fetching) return;

    if (widget) {
      if (widgetContainerRef.current && widgetContainerRef.current?.firstChild) {
        widgetContainerRef.current?.removeChild(widgetContainerRef.current?.firstChild);
      }

      // Hack to mount Widget on first render
      setTimeout(() => widget.mount('widget_container'), 500);
    }
  }, [widget, fetching]);

  useEffect(() => {
    Promise.all([getLoginWidgetParams(), getOAuthSettings(projectId), getCustomStyleURL(projectId)])
      .then(([widgetSettings, oauthSettings, customStyle]) => {
        const { countries } = widgetSettings;

        dispatch({ type: ActionType.setCustomStyleUrl, payload: customStyle.custom_style_url });
        dispatch({ type: ActionType.setLanguageOptions, payload: updatedWidgetLocale });
        dispatch({ type: ActionType.setCountryOptions, payload: countries });
        dispatch({ type: ActionType.setOauth2Options, payload: oauthSettings.clients });
      })
      .catch((error) => {
        showErrorNotify(error);
      })
      .finally(() => dispatch({ type: ActionType.setFetching, payload: false }));
  }, []);

  const toWidgetBuilder = async () => {
    enableChecklistItem(ChecklistItems.WIDGET_CUSTOMIZATION);
    history.push({
      pathname: widgetEditorPath(projectId)
    });
  };

  if (!currentProjectType || fetching) {
    return (
      <VerticalHorizontalCenterBlock>
        <Preloader />
      </VerticalHorizontalCenterBlock>
    );
  }

  return (
    <>
      {!isAgreementSigned && <AgreementNotification />}

      <HeaderTitle
        title={intl.formatMessage({ id: 'widget-customization.settings.title' })}
        subtitle={intl.formatMessage({ id: 'widget-customization.settings.subtitle' })}
        dataTestIdPrefix="customization-widget"
      />

      <CustomizationFrameWrapper>
        <SettingsBar
          label={intl.formatMessage({
            id: `widget-customization.settings.bar.select-method.${LoginMethods[currentProjectType]}`
          })}
          switchPreview={() => dispatch({ type: ActionType.togglePreview })}
          isPreview={isPreview}
          customizeHandle={toWidgetBuilder}
          disableCustomization={!isAgreementSigned}
        />
        {isPreview ? (
          <WidgetFrame
            ref={widgetContainerRef}
            id="widget_container"
            data-test-id="wc_widget-container"
          />
        ) : (
          <CodeEditorFrame>
            <CodeEditor
              disabled
              allowCopyCode
              dataTestId="wc_code-editor"
              initialValue={getExampleCode({
                projectId,
                country,
                language,
                ...additionalParams
              })}
              showLineNumbers
            />
          </CodeEditorFrame>
        )}
      </CustomizationFrameWrapper>

      <WidgetSettings state={state} dispatch={dispatch} />

      <Separator />

      <VisualSettings state={state} dispatch={dispatch} />
    </>
  );
};

export default withRouter(WidgetCustomizeFrame);
